import { documentCountries, documentCountry } from '@/utils/documentIdentity';
import { formatLocalDate } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Cliente, TipoCliente } from '@/types';
import clientService from '@/services/clientService';
import inmuebleService from '@/services/inmuebleService';
import MainLayout from '@/components/layout/MainLayout';
import Button from '@/components/common/Button';
import { getClientFullName } from '@/utils/clientUtils';
import { useAuth } from '@/contexts/AuthContext';

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [client, setClient] = useState<Cliente | null>(null);
  const [contracts, setContracts] = useState<any[]>([]);
  const [propertiesCount, setPropertiesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        if (!id) return;
        const [clientRes, contractsRes]: any = await Promise.all([
          clientService.getClientById(Number(id)),
          clientService.getClientContracts(Number(id))
        ]);

        // Handle client data
        const clientData = (clientRes as any).data || clientRes;
        setClient(clientData);

        if (clientData.tipo === TipoCliente.PROPIETARIO || clientData.tipo === TipoCliente.AMBOS) {
          const propertiesRes = await inmuebleService.getInmuebles({
            owner_id: Number(id),
            limit: 1,
          });
          const total = (propertiesRes as any).data?.total;
          setPropertiesCount(typeof total === 'number' ? total : 0);
        } else {
          setPropertiesCount(0);
        }

        // Handle contracts data - ensure it's always an array
        let contractsData = (contractsRes as any).data || contractsRes;

        // If contractsData is an object with a data property, use that
        if (contractsData && typeof contractsData === 'object' && !Array.isArray(contractsData) && contractsData.data) {
          contractsData = contractsData.data;
        }

        // Ensure it's an array
        if (Array.isArray(contractsData)) {
          setContracts(contractsData);
        } else {
          setContracts([]);
        }
      } catch (error) {
        console.error('Error loading client details:', error);
        setContracts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const getClientTypeColor = (tipo: string) => {
    const colors: Record<string, string> = {
      [TipoCliente.PROPIETARIO]: 'bg-purple-100 text-purple-800 border-purple-200',
      [TipoCliente.INQUILINO]: 'bg-blue-100 text-blue-800 border-blue-200',
      [TipoCliente.AMBOS]: 'bg-green-100 text-green-800 border-green-200',
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getClientTypeIcon = (tipo: string) => {
    const icons: Record<string, string> = {
      [TipoCliente.PROPIETARIO]: '🏠',
      [TipoCliente.INQUILINO]: '🔑',
      [TipoCliente.AMBOS]: '🏠🔑',
    };
    return icons[tipo] || '👤';
  };

  const getContractStatusColor = (estado: string) => {
    const colors: Record<string, string> = {
      ACTIVO: 'bg-green-100 text-green-800',
      VENCIDO: 'bg-red-100 text-red-800',
      FINALIZADO: 'bg-gray-100 text-gray-800',
      PENDIENTE: 'bg-yellow-100 text-yellow-800',
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return formatLocalDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!client) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👤</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Cliente no encontrado</h3>
            <Button onClick={() => navigate(-1)}>Volver</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {getClientFullName(client)}
              </h1>
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getClientTypeColor(client.tipo)}`}>
                <span>{getClientTypeIcon(client.tipo)}</span>
                {client.tipo}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${client.activo ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                {client.activo ? '✅ Activo' : '❌ Inactivo'}
              </span>
            </div>
            <p className="text-gray-600 flex items-center gap-2">
              <span>📋</span>
              {client.tipo_documento}: {client.documento}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Volver
            </Button>
            {hasPermission('clients', 'write') && (
              <Button onClick={() => navigate(`/clients/${id}/edit`)}>
                Editar Cliente
              </Button>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>👤</span>
                Información Personal
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Nombre Completo</h3>
                  <p className="text-gray-900 font-medium">{getClientFullName(client)}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Documento</h3>
                  <p className="text-gray-900">{client.tipo_documento}: {client.documento}</p>
                  <p className="text-sm text-gray-500">País para CFE: {documentCountries[documentCountry(client)] || 'Complete la nacionalidad'}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Tipo de Cliente</h3>
                  <p className="text-gray-900">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${getClientTypeColor(client.tipo)}`}>
                      {getClientTypeIcon(client.tipo)} {client.tipo}
                    </span>
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Estado</h3>
                  <p className="text-gray-900">
                    {client.activo ? '✅ Activo' : '❌ Inactivo'}
                  </p>
                </div>
              </div>

              {(client.tipo === TipoCliente.INQUILINO || client.tipo === TipoCliente.AMBOS) && (
                <div className="mt-4"><h3 className="text-sm font-medium text-gray-500">Agente de retención de IRPF como inquilino</h3><p>{client.agente_retencion_irpf ? 'Sí' : 'No'}</p></div>
              )}

              {client.notas && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Notas</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{client.notas}</p>
                </div>
              )}
            </div>

            {/* Contact Information Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>📞</span>
                Información de Contacto
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">📧</div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-500">Email</h3>
                    <p className="text-gray-900 font-medium">
                      {client.email ? (
                        <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline">
                          {client.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">No especificado</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="text-2xl">📱</div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-500">Teléfono</h3>
                    <p className="text-gray-900 font-medium">
                      {client.telefono ? (
                        <a href={`tel:${client.telefono}`} className="text-blue-600 hover:underline">
                          {client.telefono}
                        </a>
                      ) : (
                        <span className="text-gray-400">No especificado</span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="text-2xl">📍</div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-500">Dirección</h3>
                    <p className="text-gray-900">
                      {client.direccion || <span className="text-gray-400">No especificada</span>}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contract History */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>📄</span>
                Historial de Contratos
              </h2>
              {contracts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📋</div>
                  <p className="text-sm">No hay contratos asociados a este cliente</p>
                  {hasPermission('contracts', 'write') && (
                    <Button
                      variant="secondary"
                      className="mt-4"
                      onClick={() => navigate(`/contracts/new?cliente_id=${id}`)}
                    >
                      Crear Contrato
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contrato
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Inmueble
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Período
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {contracts.map((contrato) => (
                        <tr
                          key={contrato.id}
                          className="cursor-pointer hover:bg-blue-50 focus:bg-blue-50"
                          tabIndex={0}
                          role="button"
                          onClick={() => navigate(`/contracts/${contrato.id}`)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              navigate(`/contracts/${contrato.id}`);
                            }
                          }}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {contrato.numero_contrato || `#${contrato.id}`}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {contrato.inmueble_direccion || `Inmueble #${contrato.inmueble_id}`}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {formatDate(contrato.fecha_inicio)} → {formatDate(contrato.fecha_fin)}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getContractStatusColor(contrato.estado)}`}>
                              {contrato.estado}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Quick Stats & Actions */}
          <div className="space-y-6">
            {/* Quick Stats Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📄</span>
                    <span className="text-sm font-medium text-gray-700">Contratos</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate(`/contracts?cliente_id=${id}`)}
                    >
                      Ver
                    </Button>
                    <span className="text-2xl font-bold text-blue-600">{contracts.length}</span>
                  </div>
                </div>

                {(client.tipo === TipoCliente.PROPIETARIO || client.tipo === TipoCliente.AMBOS) && (
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🏠</span>
                      <span className="text-sm font-medium text-gray-700">Propiedades</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/properties?owner_id=${id}`)}
                      >
                        Ver
                      </Button>
                      <span className="text-2xl font-bold text-purple-600">{propertiesCount}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
              <div className="space-y-3">
                {hasPermission('contracts', 'write') && (
                  <Button
                    className="w-full"
                    onClick={() => navigate(`/contracts/new?cliente_id=${id}`)}
                  >
                    Nuevo Contrato
                  </Button>
                )}
                {hasPermission('clients', 'write') && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => navigate(`/clients/${id}/edit`)}
                  >
                    Editar Información
                  </Button>
                )}
                {client.email && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.location.href = `mailto:${client.email}`}
                  >
                    Enviar Email
                  </Button>
                )}
              </div>
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Información del Sistema</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">ID</p>
                  <p className="font-mono text-gray-900">#{client.id}</p>
                </div>
                {client.created_at && (
                  <div>
                    <p className="text-gray-500">Creado</p>
                    <p className="text-gray-900">{formatDate(client.created_at)}</p>
                  </div>
                )}
                {client.updated_at && (
                  <div>
                    <p className="text-gray-500">Última actualización</p>
                    <p className="text-gray-900">{formatDate(client.updated_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}


